import type {
  ColDef,
  ColGroupDef,
  SetFilterValuesFuncParams,
} from "ag-grid-community";

import { localDateFormatter } from "lib/agGrid/formatters";

import type { ColumnMeta } from "../api";
import { FORMAT_RENDERERS, boolYN } from "./shared";

export type ValuesFetcher = (colId: string) => Promise<unknown[]>;

// ─── Type classification ──────────────────────────────────────────────────────

function isNumericType(type: string): boolean {
  return /^(BIGINT|INTEGER|INT\b|INT2|INT4|INT8|INT16|INT32|INT64|DOUBLE|FLOAT|FLOAT4|FLOAT8|DECIMAL|HUGEINT|UBIGINT|UINTEGER|USMALLINT|UTINYINT|UHUGEINT|SMALLINT|TINYINT|REAL|NUMERIC)/i.test(
    type,
  );
}

function isDateType(type: string): boolean {
  return /^DATE$/i.test(type);
}

function isTimestampType(type: string): boolean {
  return /^TIMESTAMP/i.test(type);
}

function isBooleanType(type: string): boolean {
  return /^BOOLEAN$/i.test(type);
}

function isComplexType(type: string): boolean {
  return /^(STRUCT|LIST|MAP)/i.test(type) || type.includes("[");
}

// ─── Header name formatting ───────────────────────────────────────────────────

const ABBREVIATIONS: Record<string, string> = {
  Id: "ID",
  Ids: "IDs",
  Abn: "ABN",
  Pic: "PIC",
  Nlis: "NLIS",
  Cbid: "CBID",
  Gst: "GST",
  Nvd: "NVD",
  Acl: "ACL",
};

function applyAbbreviations(name: string): string {
  return name.replace(/\b[A-Z][a-z]+\b/g, word => ABBREVIATIONS[word] ?? word);
}

function formatFieldName(field: string): string {
  // For grouped fields (vendor__name) use only the last segment as the header
  const segment = field.includes("__")
    ? field.split("__").slice(1).join(" ")
    : field;
  const words = segment
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
  return applyAbbreviations(words);
}

function formatGroupName(prefix: string): string {
  const words = prefix
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
  return applyAbbreviations(words);
}

// ─── Single-column inference ─────────────────────────────────────────────────

/**
 * Infers a single AG Grid ColDef from a ColumnMeta descriptor returned by the
 * `/meta` endpoint. Maps DuckDB types to AG Grid filters, formatters, and
 * default visibility. `fetchValues` is wired into set-filter params so the
 * server-side distinct-values endpoint is called on demand for text/boolean columns.
 *
 * DuckDB returns DATE columns as ISO strings ("2024-01-15") and TIMESTAMP columns as
 * "2024-01-15 10:30:00" — localDateFormatter handles both via new Date().
 */
function inferColDef(col: ColumnMeta, fetchValues?: ValuesFetcher): ColDef {
  const numeric = isNumericType(col.type);
  const date = isDateType(col.type);
  const timestamp = isTimestampType(col.type);
  const boolean = isBooleanType(col.type);
  const complex = isComplexType(col.type);
  const isId = /(_id|_cbid)$/i.test(col.field) || col.field === "id";
  const isText = !numeric && !date && !timestamp && !boolean && !complex;

  const formatRenderer = col.format ? FORMAT_RENDERERS[col.format] : undefined;

  function inferValueFormatter() {
    if (formatRenderer) return formatRenderer;
    if (date || timestamp) return localDateFormatter;
    if (boolean) return boolYN;
    return undefined;
  }

  function inferFilter(): ColDef["filter"] {
    // Check complex first — numeric-prefix types like INT[] must not match agNumberColumnFilter.
    if (complex) return false;
    if (numeric) return "agNumberColumnFilter";
    if (date || timestamp) return "agDateColumnFilter";
    if (isText || boolean) return "agSetColumnFilter";
    return false;
  }

  const valueFormatter = inferValueFormatter();
  const filter = inferFilter();

  let filterParams: ColDef["filterParams"] = undefined;
  if (boolean) {
    filterParams = { values: [true, false], valueFormatter: boolYN };
  } else if (isText && fetchValues) {
    const { field } = col;
    filterParams = {
      values: (params: SetFilterValuesFuncParams) => {
        fetchValues(field)
          .then(vals => params.success(vals as string[]))
          .catch(() => params.success([]));
      },
    };
  }

  return {
    field: col.field,
    headerName: col.label ?? formatFieldName(col.field),
    type: numeric && !isId ? "rightAligned" : undefined,
    enableValue: numeric,
    enableRowGroup: !numeric && !complex,
    valueFormatter,
    filter,
    ...(filterParams ? { filterParams } : {}),
    hide: col.hide ?? (isId || complex),
  };
}

// ─── Public builder ───────────────────────────────────────────────────────────

/**
 * Builds AgGrid column definitions from the meta endpoint's column list.
 *
 * Columns with a double-underscore prefix (vendor__, buyer__, etc.) are
 * grouped into ColGroupDef entries automatically. Curated overrides are merged
 * per-field to add currency/weight formatters or adjust aggFunc.
 */
export function buildColumnDefs(
  columns: ColumnMeta[],
  fetchValues?: ValuesFetcher,
): (ColDef | ColGroupDef)[] {
  const groups = new Map<string, ColDef[]>();
  const ungrouped: ColDef[] = [];

  for (const col of columns) {
    const def = inferColDef(col, fetchValues);

    const sepIdx = col.field.indexOf("__");
    if (sepIdx > 0) {
      const prefix = col.field.slice(0, sepIdx);
      if (!groups.has(prefix)) groups.set(prefix, []);
      groups.get(prefix)!.push(def);
    } else {
      ungrouped.push(def);
    }
  }

  const groupDefs: ColGroupDef[] = Array.from(groups.entries()).map(
    ([prefix, children]) => ({
      headerName: formatGroupName(prefix),
      children,
    }),
  );

  return [...ungrouped, ...groupDefs];
}
